import React, { useState, useEffect, useRef } from "react";
import askQuestion from "../../services/chatbotService";
import LogoSpc from "../../assets/images/logo.png";
import Close from "../../assets/svg/close-button.svg";
import BotIcon from "../../assets/svg/robot.svg";
import SendIcon from "../../assets/svg/send.svg";
import Copy from "../../assets/svg/copy.svg";
import LoadingIcon from "../../assets/images/loadAnimation.gif";
import "./ChatWindow.css";

const ChatWindow = ({ isOpen, toggleChat }) => {
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState("");
  const [alert, setAlert] = useState("");
  const [loading, setLoading] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const initialMessage = {
      type: "bot",
      text: "Olá! Seja bem vindo ao Portal de API. Em que podemos ajudar?",
      initial: true,
    };
    setMessages([initialMessage]);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const handleAsk = async () => {
    if (!question) return;

    const userQuestion = {
      type: "user",
      text: question,
    };
    setMessages((prevMessages) => [...prevMessages, userQuestion]);
    setLoading(true);

    try {
      const data = await askQuestion(question);

      if (data.answer) {
        const botMessage = {
          type: "bot",
          text: "",
          initial: false,
        };
        setMessages((prevMessages) => [...prevMessages, botMessage]);

        typeText(botMessage, data.answer);
      } else {
        setError("Desculpe, não entendi sua pergunta.");
      }
    } catch (error) {
      setError(error.message);
    }

    setLoading(false);
    setQuestion("");
  };

  const typeText = (message, fullText) => {
    let response = fullText;
    if (!response.includes("```")) {
      response = response.replaceAll('\n{\n', '\n\n```\n{\n').replaceAll('\n}\n', '\n}\n```\n');
    }
    const messageParts = response.split(/(```[\s\S]*?```)/g);
    let currentPart = 0;
    let currentIndex = 0;

    const typeNextChar = () => {
      if (currentPart >= messageParts.length) return;

      const part = messageParts[currentPart];
      const isCode = part.startsWith("```") && part.endsWith("```");

      if (isCode) {
        message.text += part;
        setMessages((prevMessages) => [...prevMessages]);
        currentPart++;
        currentIndex = 0;

        messagesEndRef.current?.scrollIntoView({
          behavior: "auto",
          block: "end",
          inline: "nearest",
        });
        typeNextChar();
      } else {
        if (currentIndex < part.length) {
          message.text += part[currentIndex];
          setMessages((prevMessages) => [...prevMessages]);
          currentIndex++;

          messagesEndRef.current?.scrollIntoView({
            behavior: "auto",
            block: "end",
            inline: "nearest",
          });

          setTimeout(typeNextChar, 5);
        } else {
          currentPart++;
          currentIndex = 0;
          typeNextChar();
        }
      }
    };

    typeNextChar();
  };

  const handleInputChange = (e) => {
    setQuestion(e.target.value);
    setError(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleAsk();
      messagesEndRef.current?.scrollIntoView({
        behavior: "auto",
        block: "end",
        inline: "nearest",
      });
      setQuestion("");
    }
  };

  const renderMessage = (msg, index) => {
    const isBotMessage = msg.type === "bot";

    const handleCopyMessage = (codeContent) => {
      navigator.clipboard.writeText(codeContent);
      setAlert("Texto copiado");
      setTimeout(() => setAlert(""), 1000);
    };

    const messageParts = msg.text.split(/(```[\s\S]*?```)/g);

    return (
      <div className={`message ${msg.type}`} key={index}>
        {isBotMessage && <img className="icon" src={BotIcon} alt="bot" />}
        <div className="code-container">
          {messageParts.map((part, i) => {
            if (part.startsWith("```") && part.endsWith("```")) {
              const codeContent = part.replace(/```/g, "");
              return (
                <div className="code-block" key={i}>
                  <pre>{codeContent}</pre>
                  <button onClick={() => handleCopyMessage(codeContent)}>
                    <img src={Copy} alt="copy" title="Copiar" />
                  </button>
                </div>
              );
            } else {
              return (
                <p className="message-text" key={i}>
                  {part}
                </p>
              );
            }
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="chat-window">
      <div className="chat-header">
        <img className="logo" src={LogoSpc} alt="SPC GRAFENO" />
        <img
          className="close-button"
          src={Close}
          alt="Close"
          onClick={toggleChat}
        />
      </div>
      <div className="chat-body">
        <div className="chat-messages">
          {messages.map(renderMessage)}
          {loading && (
            <div className="message bot">
              <img className="icon" src={BotIcon} alt="bot" />
              <p className="loading-text">
                <img src={LoadingIcon} alt="loading" />
              </p>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>
      {alert && <p className="alert">{alert}</p>}
      {error && <p className="error">{error}</p>}
      <div className="chat-footer">
        <div className="input-container">
          <input
            type="text"
            value={question}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            placeholder="Tire sua dúvida"
            ref={inputRef}
          />

          <button onClick={handleAsk}>
            <img src={SendIcon} alt="Send" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatWindow;
